// =====================================================
// IMPORTS
// =====================================================

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Table,
    Card,
    Row,
    Col,
    Statistic,
    Button,
    Space,
    Typography,
    Spin,
    Empty,
    message,
    Tag,
} from "antd";

import {
    DownloadOutlined,
    DollarCircleOutlined,
    WalletOutlined,
    FileTextOutlined,
    ReloadOutlined,
} from "@ant-design/icons";

import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import api from "../../api/api";
import {
    useAcademicYear
} from "../../AcademicYearContext";
import StudentPaymentHistoryPDF from "./StudentPaymentHistoryPDF";


const { Title } = Typography;

// =====================================================
// COMPONENT
// =====================================================

const TransActionPerStudent = ({
    selectedChild,
    onBack
}) => {
    const { selectedYear } =
        useAcademicYear();

    // =====================================================
    // STATES
    // =====================================================

    const [loading, setLoading] =
        useState(false);

    const [downloading, setDownloading] =
        useState(false);

    const [student, setStudent] =
        useState(null);

    const [transactions, setTransactions] =
        useState([]);

    const [academicYear, setAcademicYear] =
        useState(null);

    // =====================================================
    // HELPERS
    // =====================================================

    const money = useCallback((value) => {

        return Number(
            value || 0
        ).toLocaleString(
            undefined,
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    }, []);




    const formatDate = useCallback((date) => {

        if (!date)
            return "-";

        return new Date(date)
            .toLocaleDateString();

    }, []);





    // =====================================================
    // FETCH TRANSACTIONS
    // =====================================================

    const fetchTransactions =
        useCallback(async () => {

            if (!selectedYear?.id)
                return;

            setLoading(true);

            try {

                const response =
                    await api.get(
                        "/student-payment-history/",
                        {
                            params: {
                                academic_year:
                                    selectedYear.id,
                                student: selectedChild.id,
                            },
                        }
                    );



const data = response.data;


setStudent({
    id: selectedChild.id,
    full_name: selectedChild.full_name,
    grade_class_name:
        selectedChild.grade_class_name,
    student_image:
        selectedChild.student_admission_image
});


setTransactions(
    data.results || []
);


setAcademicYear(
    selectedYear
);

            } catch (error) {

                console.error(error);

                message.error(
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Unable to load payment transactions."
                );

            } finally {

                setLoading(false);

            }

       }, [
    selectedYear,
    selectedChild
]);



    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        fetchTransactions();

    }, [fetchTransactions]);



    // =====================================================
    // TOTALS
    // =====================================================

    const totalPaid =
        useMemo(() => {

            return transactions.reduce(

                (sum, item) =>

                    sum +
                    Number(
                        item.amount_paid || 0
                    ),

                0

            );

        }, [transactions]);




    const outstandingBalance =
        useMemo(() => {

            return transactions.reduce(

                (sum, item) =>

                    sum +
                    Number(
                        item.balance || 0
                    ),

                0

            );

        }, [transactions]);





    const totalTransactions =
        transactions.length;



    // =====================================================
    // PDF DOWNLOAD
    // =====================================================

    // const downloadPDF =
    //     async () => {

    //         if (!student) {

    //             message.warning(
    //                 "Student information not found."
    //             );

    //             return;

    //         }

    //         setDownloading(true);

    //         try {

    //             const blob =
    //                 await pdf(

    //                     <StudentPaymentHistoryPDF
    //                         student={student}
    //                         transactions={transactions}
    //                         academicYear={
    //                             academicYear
    //                         }
    //                     />

    //                 ).toBlob();

    //             saveAs(

    //                 blob,

    //                 `${student.full_name
    //                     ?.replaceAll(
    //                         " ",
    //                         "_"
    //                     )}_Payment_Statement.pdf`

    //             );

    //             message.success(
    //                 "Payment statement downloaded successfully."
    //             );

    //         } catch (error) {

    //             console.error(error);

    //             message.error(
    //                 "Unable to generate PDF."
    //             );

    //         } finally {

    //             setDownloading(false);

    //         }

    //     };



    // =====================================================
// PDF DOWNLOAD FLOW
// =====================================================
const downloadPDF = async () => {
    // ---------------------------------------------
    // Make sure student data exists
    // ---------------------------------------------
    if (!student) {
        message.warning("Student information not found.");
        return;
    }

    setDownloading(true);

    try {
        // ---------------------------------------------
        // Create PDF from existing component
        // ---------------------------------------------
        const document = (
            <StudentPaymentHistoryPDF
                student={student}
                transactions={transactions}
                academicYear={academicYear}
            />
        );

        // ---------------------------------------------
        // Convert PDF to Blob
        // ---------------------------------------------
        const blob = await pdf(document).toBlob();

        // ---------------------------------------------
        // Download
        // ---------------------------------------------
        saveAs(
            blob,
            `${student.full_name?.replace(/\s+/g, "_")}_Payment_Statement.pdf`
        );

        message.success("Payment statement downloaded successfully.");
    } catch (error) {
        console.error(error);
        message.error("Failed to generate payment statement.");
    } finally {
        setDownloading(false);
    }
};



    // =====================================================
    // TABLE COLUMNS
    // =====================================================

    const columns = [

        {
            title: "#",

            width: 70,

            render: (
                _,
                __,
                index
            ) => index + 1,
        },

        {
            title: "Date",

            dataIndex: "date",

            render: formatDate,
        },

        {
            title: "Category",

            dataIndex:
                "payment_category",

            render: (text) =>

                text || "-",
        },

        {
            title: "Description",

            dataIndex:
                "fee_type",

            render: (text) =>

                text || "-",
        },

        {
            title: "Amount Paid",

            dataIndex:
                "amount_paid",

            align: "right",

            render: (value) => (

                <Tag color="green">

                    $
                    {money(value)}

                </Tag>

            ),
        },

        {
            title: "Balance",

            dataIndex:
                "balance",

            align: "right",

            render: (value) => (

                <Tag color="red">

                    $
                    {money(value)}

                </Tag>

            ),
        },

        {
            title: "Processed By",

            dataIndex:
                "created_by",

            render: (value) =>
                value || "-",
        },

    ];
    // =====================================================
    // HEADER ACTIONS
    // =====================================================

    const HeaderActions = () => (

        <Space wrap>

            <Button
                icon={<ReloadOutlined />}
                onClick={fetchTransactions}
            >
                Refresh
            </Button>

            {/* <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={downloadPDF}
            >
                Download Statement
            </Button> */}
            <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={downloadPDF}
            >
                Download Payment Statement
            </Button>

        </Space>

    );



    // =====================================================
    // STUDENT SUMMARY
    // =====================================================

    const StudentSummary = () => (

        <Card
            style={{
                marginBottom: 20,
                borderRadius: 12,
            }}
        >

            <Row
                gutter={[24, 24]}
                align="middle"
            >

                <Col
                    xs={24}
                    md={14}
                >

                    <Title
                        level={4}
                        style={{
                            marginBottom: 6,
                        }}
                    >
                        {student?.full_name ||
                            "Student"}
                    </Title>

                    <Space
                        direction="vertical"
                        size={2}
                    >

                        <span>
                            <strong>
                                Admission No:
                            </strong>{" "}
                            {student?.id ||
                                "-"}
                        </span>

                        <span>
                            <strong>
                                Grade:
                            </strong>{" "}
                            {student?.grade_class_name ||
                                "-"}
                        </span>

                        <span>
                            <strong>
                                Academic Year:
                            </strong>{" "}
                            {academicYear?.name ||
                                "-"}
                        </span>

                    </Space>

                </Col>

                <Col
                    xs={24}
                    md={10}
                    style={{
                        textAlign:
                            "right",
                    }}
                >

                    <HeaderActions />

                </Col>

            </Row>

        </Card>

    );



    // =====================================================
    // STATISTICS
    // =====================================================

    const StatisticsCards = () => (

        <Row gutter={[20, 20]}>

            <Col
                xs={24}
                sm={12}
                lg={8}
            >

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                    }}
                >

                    <Statistic
                        title="Total Paid"
                        value={totalPaid}
                        precision={2}
                        prefix="$"
                        valueStyle={{
                            color:
                                "#16A34A",
                        }}
                        icon={
                            <DollarCircleOutlined />
                        }
                    />

                </Card>

            </Col>

            <Col
                xs={24}
                sm={12}
                lg={8}
            >

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                    }}
                >

                    <Statistic
                        title="Outstanding Balance"
                        value={
                            outstandingBalance
                        }
                        precision={2}
                        prefix="$"
                        valueStyle={{
                            color:
                                "#DC2626",
                        }}
                        icon={
                            <WalletOutlined />
                        }
                    />

                </Card>

            </Col>

            <Col
                xs={24}
                sm={24}
                lg={8}
            >

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                    }}
                >

                    <Statistic
                        title="Transactions"
                        value={
                            totalTransactions
                        }
                        valueStyle={{
                            color:
                                "#2563EB",
                        }}
                        icon={
                            <FileTextOutlined />
                        }
                    />

                </Card>

            </Col>

        </Row>

    );



    // =====================================================
    // TRANSACTION TABLE
    // =====================================================

    const TransactionsTable = () => (

        <Card
            title="Payment Transactions"
            style={{
                marginTop: 24,
                borderRadius: 12,
            }}
        >

            <Table
                rowKey={(record, index) =>
                    record.id || index
                }
                columns={columns}
                dataSource={transactions}
                loading={loading}
                bordered
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (
                        total
                    ) =>
                        `Total ${total} Transactions`,
                }}
                scroll={{
                    x: 1100,
                }}
                locale={{
                    emptyText: (
                        <Empty
                            description="No payment transactions found."
                        />
                    ),
                }}
            />

        </Card>

    );
    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div
            style={{
                padding: 24,
            }}
        >

            {/* ============================================
                PAGE TITLE
            ============================================ */}

            <div
                style={{
                    marginBottom: 24,
                }}
            >

                <Title
                    level={2}
                    style={{
                        marginBottom: 4,
                    }}
                >
                    My Payment Transactions
                </Title>

                <p
                    style={{
                        color: "#6B7280",
                        margin: 0,
                        fontSize: 15,
                    }}
                >
                    View all your payment history and download your official payment statement.
                </p>

            </div>



            {/* ============================================
                LOADING
            ============================================ */}

            {

                loading ? (

                    <Card
                        style={{
                            borderRadius: 12,
                        }}
                    >

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: 350,
                            }}
                        >

                            <Spin size="large" />

                        </div>

                    </Card>

                ) : !student ? (

                    <Card
                        style={{
                            borderRadius: 12,
                        }}
                    >

                        <Empty
                            description="No student payment information found."
                        />

                    </Card>

                ) : (

                    <>

                        {/* ============================================
                            STUDENT INFORMATION
                        ============================================ */}

                        <StudentSummary />



                        {/* ============================================
                            STATISTICS
                        ============================================ */}

                        <StatisticsCards />



                        {/* ============================================
                            TRANSACTIONS TABLE
                        ============================================ */}

                        <TransactionsTable />

                    </>

                )

            }

        </div>

    );

};

// =====================================================
// EXPORT
// =====================================================

export default TransActionPerStudent;